import { UserManager, WebStorageStateStore } from "oidc-client-ts";

// Get client code from subdomain
function getClientCode(): string {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    if (parts.length >= 3) {
      return parts[0]; // e.g., "bdconsolepartner3"
    }
  }
  return "policyportal";
}

// Get base domain
function getBaseDomain(): string {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    // Return everything after the client code
    // e.g., "policyportal.highbond.localhost" or "policyportal-api.highbond-s3.com"
    return parts.slice(1).join(".");
  }
  return "policyportal.highbond.localhost";
}

// Get protocol based on environment
function getProtocol(): string {
  if (typeof window !== "undefined") {
    return window.location.protocol === "https:" ? "https" : "http";
  }
  return "http";
}

// Get port for local development
function getPort(): string {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("localhost")) {
      return ":3100"; // Policy portal app runs on 3100
    }
  }
  return "";
}

// DiligentOne Dev platform domains. A `-uk` host on one of these is a DR standby of Dev,
// not a region of its own, and resolves its identity host differently — see below.
const DEV_PLATFORM_DOMAINS = ["diligentoneplatform-dev.com", "highbond-s3.com"];

// Determine region from the policyportal[-eu|-uk|-au] subdomain. Exported (pure, no window
// dependency) so region resolution can be unit-tested without mocking window.location.
export function resolveOidcRegionFromBaseDomain(baseDomain: string): string {
  if (baseDomain.includes("policyportal-uk")) {
    // Dev / eu-west-2 is a DR standby of Dev / us-west-2. DiligentOne's *global* identity
    // service fails over to `oidc-uk`, but the *regional* 'us' service this portal consumes
    // fails over to `oidc-us-uk` — that is where the `policy_manager` client and its `-uk`
    // callback are registered. Both `oidc-uk` and `oidc-us` answer 400 Bad Request for that
    // client, so the DR portal never reaches a login page. Prod UK is a region in its own
    // right and keeps plain `uk`.
    return DEV_PLATFORM_DOMAINS.some((domain) => baseDomain.endsWith(domain))
      ? "us-uk"
      : "uk";
  }
  if (baseDomain.includes("policyportal-eu")) return "eu";
  if (baseDomain.includes("policyportal-au")) return "au";
  return "us";
}

// Generate OIDC provider URL based on the current domain
function getOidcProviderUrl(): string {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    // For localhost, always use US highbond-s3.com
    if (hostname.includes("localhost")) {
      return "https://oidc-us.highbond-s3.com";
    }

    // For AWS environments, extract region and base domain
    const baseDomain = getBaseDomain();

    // Pattern: {client}.policyportal[-eu|-uk|-au].{baseDomain}
    const region = resolveOidcRegionFromBaseDomain(baseDomain);

    // Extract the actual domain (everything after policyportal/policyportal-eu/policyportal-uk)
    // e.g., "policyportal.highbond-s3.com" → "highbond-s3.com"
    // e.g., "policyportal-eu.highbond-s3.com" → "highbond-s3.com"
    const domainParts = baseDomain.split(".");
    const actualDomain = domainParts.slice(1).join(".");

    return `https://oidc-${region}.${actualDomain}`;
  }

  // Fallback for SSR or non-browser environments
  return "https://oidc-us.highbond-s3.com";
}

// Build full base URL
function getBaseUrl(): string {
  const protocol = getProtocol();
  const clientCode = getClientCode();
  const baseDomain = getBaseDomain();
  const port = getPort();

  return `${protocol}://${clientCode}.${baseDomain}${port}`;
}

// OIDC Provider URL - dynamically generated based on current domain
const OIDC_PROVIDER_URL = getOidcProviderUrl();

const BASE_URL = getBaseUrl();

// Check if running on localhost (use proxy to avoid CORS issues)
const isLocalhost =
  typeof window !== "undefined" &&
  window.location.hostname.includes("localhost");

// Log OIDC configuration strategy
if (typeof window !== "undefined") {
  console.log(
    `🔐 OIDC Config: ${
      isLocalhost
        ? "Using Vite proxy (localhost)"
        : "Direct to OIDC provider (production)"
    }`
  );
}

// OIDC Configuration
export const oidcConfig = {
  authority: OIDC_PROVIDER_URL,
  // Use proxy for localhost (CORS), direct calls for production (CORS configured)
  metadata: {
    issuer: OIDC_PROVIDER_URL,
    authorization_endpoint: `${OIDC_PROVIDER_URL}/auth`,
    // Localhost: Use Vite proxy to avoid CORS issues
    // Production: Call OIDC provider directly (CORS is configured)
    token_endpoint: isLocalhost
      ? `${BASE_URL}/api/oidc/token`
      : `${OIDC_PROVIDER_URL}/token`,
    userinfo_endpoint: isLocalhost
      ? `${BASE_URL}/api/oidc/userinfo`
      : `${OIDC_PROVIDER_URL}/userinfo`,
    jwks_uri: isLocalhost
      ? `${BASE_URL}/api/oidc/jwks`
      : `${OIDC_PROVIDER_URL}/jwks`,
    end_session_endpoint: `${OIDC_PROVIDER_URL}/session/end`, // Always direct
  },
  client_id: "policy_manager",
  redirect_uri: `${BASE_URL}/Login/Callback`,
  post_logout_redirect_uri: BASE_URL, // No trailing slash to match registered URI
  response_type: "code",
  // Request all available scopes for maximum data
  scope: "openid profile email membership",
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Enable silent token refresh
  silent_redirect_uri: `${BASE_URL}/silent-callback.html`,
  automaticSilentRenew: false, // Manual refresh on demand
  silentRequestTimeoutInSeconds: 30,
  filterProtocolClaims: true,
  loadUserInfo: false, // Don't load userinfo during silent renew
  monitorSession: true, // Enable session monitoring for Single Logout (SLO)
  monitorAnonymousSession: false, // Only monitor when logged in
};

// Create and export UserManager instance
export const userManager = new UserManager(oidcConfig);

// Helper functions
export const login = () => {
  console.log("🔐 Initiating OIDC login...");
  return userManager.signinRedirect();
};

// Auto-login with SSO hint (for seamless authentication)
export const autoLoginSSO = () => {
  console.log("🔄 Auto-login with SSO (no credentials)...");
  // Use prompt=none to prevent login form
  // If SSO fails, OIDC provider will redirect back with error
  return userManager.signinRedirect({
    prompt: "none",
    // Optional: Add login_hint if we know the user
  });
};

export const logout = () => {
  console.log("🚪 Initiating OIDC logout...");
  return userManager.signoutRedirect();
};

export const handleCallback = async () => {
  try {
    console.log("📥 Processing OIDC callback...");
    const user = await userManager.signinRedirectCallback();
    console.log("✅ OIDC callback processed successfully");
    return user;
  } catch (error) {
    console.error("❌ Error handling OIDC callback:", error);
    throw error;
  }
};

export const getUser = () => {
  return userManager.getUser();
};

// Check if user is already logged in via silent sign-in (SSO check)
// Returns: { user, hasOidcSession }
// - user: User object if successfully authenticated, null otherwise
// - hasOidcSession: Always false when user is null (can't reliably detect cross-app sessions)
export const checkExistingSession = async (): Promise<{
  user: any;
  hasOidcSession: boolean;
}> => {
  try {
    console.log("🔍 Checking for existing OIDC session...");

    // Try silent sign-in with explicit prompt=none
    const user = await userManager.signinSilent({
      prompt: "none",
      scope: oidcConfig.scope,
    });

    if (user) {
      console.log("✅ Existing OIDC session found!");
      console.log("👤 User:", user.profile?.name || user.profile?.email);
      return { user, hasOidcSession: true };
    }

    console.log("❌ No OIDC session found");
    return { user: null, hasOidcSession: false };
  } catch (error: any) {
    console.log("❌ Silent SSO failed:", error.error || error.message);

    // Detect if user has active OIDC session (logged into other app)
    // These specific errors indicate an active session but need authorization/consent:
    // - "login_required": User has session but needs to authorize this app
    // - "consent_required": User has session but needs to grant permissions
    // - "interaction_required": User has session but needs some interaction
    const hasOidcSession =
      error.error === "login_required" ||
      error.error === "consent_required" ||
      error.error === "interaction_required";

    if (hasOidcSession) {
      console.log("✅ OIDC session detected! (logged into another app)");
      console.log(
        "💡 Will auto-redirect for seamless SSO (NO credentials needed!)"
      );
    } else {
      console.log("ℹ️  No OIDC session found - showing public portal");
    }

    return { user: null, hasOidcSession };
  }
};

// Refresh the access token
export const refreshToken = async () => {
  try {
    console.log("🔄 Attempting silent token refresh...");
    const user = await userManager.signinSilent();
    console.log("✅ Token refreshed successfully!");
    return user;
  } catch (error: any) {
    console.error("❌ Token refresh failed:", error);
    throw new Error(
      `Unable to refresh token: ${
        error.message || "Unknown error"
      }. Please log in again.`
    );
  }
};

// Get access token for API calls
// NOTE: We're returning the ID token instead of access token because:
// - The access token from the OIDC provider is OPAQUE (not a JWT)
// - The backend expects a JWT that it can validate using JWKS
// - The ID token is a JWT containing all user claims needed by the backend
// This is a common pattern in SPAs even though technically the access token
// should be used for API authorization.
export const getAccessToken = async (): Promise<string | null> => {
  const user = await getUser();
  if (!user || !user.id_token) {
    return null;
  }

  // Check if token is expired
  const now = Math.floor(Date.now() / 1000);
  if (user.expires_at && user.expires_at <= now) {
    console.log("⚠️ Token expired, attempting refresh...");
    try {
      const refreshedUser = await refreshToken();
      return refreshedUser?.id_token || null;
    } catch (error) {
      console.error("❌ Token refresh failed:", error);

      // IMPORTANT: Clean up stale tokens from localStorage
      // This prevents the app from thinking there's a valid session
      console.log("🗑️ Cleaning up expired OIDC session from localStorage");
      await userManager.removeUser();

      return null;
    }
  }

  return user.id_token;
};
